// Constantes que o site, o servidor e os scripts precisam ver iguais.
//
// Elas não moram em docs.config.ts porque server.ts é compilado por um tsconfig
// que não aceita import com extensão .ts, e docs.config importa os tipos assim.
// Este arquivo não importa nada, então atravessa os três programas sem atrito.
//
// O que ganha morando aqui: o padrão da URL do site estava escrito em quatro
// lugares e a URL do spec, em três — uma delas com o caminho errado
// (`/v3/api-docs`, que responde 404 desde sempre; medido em 17/09/2026).

/**
 * URL pública do site, usada quando o ambiente não injeta SITE_URL.
 *
 * É padrão de verdade, não conveniência de quem escreveu: se vazar para
 * produção continua correta.
 */
export const SITE_URL_PADRAO = "https://developers.infotravel.com.br";

/**
 * Endpoint do OpenAPI do infotravel em produção.
 *
 * `https://api.infotravel.com.br/v3/api-docs`, que o README e o texto de uso do
 * pull-openapi anunciavam, devolve 404. O caminho que responde é este.
 */
export const OPENAPI_SPEC_URL = "https://api.infotravel.com.br/infotravel/api/v1/api-docs";

// ── Sandbox compartilhado ────────────────────────────────────────────────────
// Único lugar onde as credenciais e URLs do sandbox existem. Tudo o mais deriva
// daqui: os tokens `{SANDBOX_*}` dos .mdx (src/lib/runtime-config.ts), o
// componente <Snippets>, e o public/demos/sandbox.js que as demos do motor
// carregam (plugins/sandbox-demo-config.ts) e o `sandbox: true` das tools do B2C no /api/mcp.
// Mudou a senha? Muda aqui, só.
export const sandbox = {
  hostname: "sandbox.infotravel.com.br",
  host: "https://sandbox.infotravel.com.br",
  apiBase: "https://sandbox.infotravel.com.br/infotravel/api/v1",
  motorApi: "https://sandbox.infotravel.com.br/integra/api",
  motorScript: "https://sandbox.infotravel.com.br/b2c/motor/v1",
  loginUrl: "https://sandbox.infotravel.com.br/infotravel/public/login.xhtml",
  b2cHost: "sandbox.otaapp.com.br",
  b2cUrl: "https://sandbox.otaapp.com.br/b2c",
  client: "SAND",
  agency: "5471",
  username: "sandbox.ws",
  password: "!sdb2022#",
  motorKey: "168B7657DD566E7B96721B488684A146",
} as const;

// ── Serviços compartilhados de produção ──────────────────────────────────────
// URLs que são as mesmas para todo cliente (não mudam com o tenant). O motor
// v2 é um só; a API REST e a loja, não — essas são por domínio do cliente.
export const endpoints = {
  motorHost: "https://motorv2.infotravel.com.br",
  motorApi: "https://motorv2.infotravel.com.br/integra/api",
  /** De onde `npm run openapi:pull` e o pod baixam o spec. Ver shared/constants.ts. */
  openapiSpec: OPENAPI_SPEC_URL,
  /** URL pública do site quando o ambiente não injeta SITE_URL. */
  siteUrl: SITE_URL_PADRAO,
} as const;

// ── Valores de exemplo ───────────────────────────────────────────────────────
// O mesmo token, o mesmo hotel e a mesma reserva atravessam quickstart,
// autenticação, fluxos comuns e webhooks em três idiomas. Repetidos à mão eles
// divergiram: um trecho seguia a reserva 778213 e o seguinte, outra.
//
// Chegam no conteúdo como tokens `{EXAMPLE_*}` (src/lib/runtime-config.ts), que
// só valem dentro de bloco de código, code inline e link. Em prosa corrida
// continue escrevendo o valor: ali ele quase sempre vem acompanhado de uma
// explicação ("86400 segundos, 24 horas") que não sai de uma constante.
//
// Estes são exemplos, não fatos medidos. Os ids de região e aeroporto da página
// de erros (2802, 23500, 2732, 8249) ficam onde estão: cada um é a prova de uma
// frase específica sobre os dados reais e trocá-los quebraria o argumento.
export const examples = {
  token: "b97eb660-2f17-4b37-bb47-0ffc30f18772",
  /** O que o sandbox devolve hoje em `expire_seconds`. Quem decide é o servidor. */
  expireSeconds: "86400",
  hotel: "207684",
  roomKey: "rm_9f2a",
  booking: "778213",
  contactName: "Marcela",
  contactLastName: "Andrade",
  contactEmail: "marcela@rioreceptivo.com",
} as const;

// ── Tokens ───────────────────────────────────────────────────────────────────
// O mapa que troca `{SANDBOX_API}` pelo valor real. Vive aqui, e não em
// src/lib/, porque três programas precisam dele: o site (CodeBlock, code inline
// e links dos .mdx), o servidor MCP (os guias de mcp/guides, que são .md lidos
// em runtime e entregues a um agente) e os scripts.
//
// `{SITE_URL}` e `{MCP_URL}` não estão aqui: dependem do ambiente e cada lado os
// acrescenta em cima (src/lib/runtime-config.ts no browser, server.ts no pod).
export const tokensEstaticos: Record<string, string> = {
  "{SANDBOX_API}": sandbox.apiBase,
  "{SANDBOX_HOST}": sandbox.host,
  "{SANDBOX_HOSTNAME}": sandbox.hostname,
  "{SANDBOX_MOTOR_API}": sandbox.motorApi,
  "{SANDBOX_MOTOR_JS}": sandbox.motorScript,
  "{SANDBOX_LOGIN_URL}": sandbox.loginUrl,
  "{SANDBOX_B2C}": sandbox.b2cUrl,
  "{SANDBOX_B2C_HOST}": sandbox.b2cHost,
  "{SANDBOX_CLIENT}": sandbox.client,
  "{SANDBOX_AGENCY}": sandbox.agency,
  "{SANDBOX_USER}": sandbox.username,
  "{SANDBOX_PASSWORD}": sandbox.password,
  "{SANDBOX_MOTOR_KEY}": sandbox.motorKey,
  "{EXAMPLE_TOKEN}": examples.token,
  "{EXAMPLE_EXPIRE}": examples.expireSeconds,
  "{EXAMPLE_HOTEL}": examples.hotel,
  "{EXAMPLE_ROOM_KEY}": examples.roomKey,
  "{EXAMPLE_BOOKING}": examples.booking,
  "{EXAMPLE_NAME}": examples.contactName,
  "{EXAMPLE_LAST_NAME}": examples.contactLastName,
  "{EXAMPLE_EMAIL}": examples.contactEmail,
  "{MOTOR_HOST}": endpoints.motorHost,
  "{MOTOR_API}": endpoints.motorApi,
};

// Tokens que dependem do idioma da página. Um leitor brasileiro espera
// `seu-dominio.com.br` onde o inglês espera `your-domain.com`; escrever o
// placeholder à mão em cada página é como o repositório acabou com seis grafias
// para a mesma coisa (`seu-sistema.com.br`, `meusite.com.br`, `SEU_DOMINIO`…).
//
// `{YOUR_DOMAIN}` é o domínio Infotravel do cliente (onde a API e o B2C dele
// respondem); `{YOUR_SYSTEM}` é o servidor do próprio cliente, o que recebe um
// callback. São coisas diferentes e por isso são dois tokens.
export const tokensPorIdioma: Record<string, Record<string, string>> = {
  pt: { "{YOUR_DOMAIN}": "seu-dominio.com.br", "{YOUR_SYSTEM}": "seu-sistema.com.br" },
  en: { "{YOUR_DOMAIN}": "your-domain.com", "{YOUR_SYSTEM}": "your-system.com" },
  es: { "{YOUR_DOMAIN}": "tu-dominio.com", "{YOUR_SYSTEM}": "tu-sistema.com" },
};

/**
 * Troca os tokens de um texto.
 *
 * `extras` é para o que só o chamador sabe (`{SITE_URL}`, `{MCP_URL}`).
 * `locale` escolhe a variante de `{YOUR_DOMAIN}`; sem ele, português.
 */
export function expandirTokens(texto: string, extras?: Record<string, string>, locale?: string): string {
  if (!texto.includes("{")) return texto;
  let saida = texto;
  const doIdioma: Record<string, string> = tokensPorIdioma[locale ?? ""] ?? tokensPorIdioma.pt ?? {};
  for (const mapa of [tokensEstaticos, doIdioma, extras ?? {}])
    for (const [token, valor] of Object.entries(mapa)) saida = saida.split(token).join(valor);
  return saida;
}
